import React from "react";
import { motion } from "framer-motion";
import { X, Check, ArrowRight } from "lucide-react";
import { fadeUp, stagger, viewport } from "@/lib/motion";
import { FRAGMENTED, CONSOLIDATED } from "@/data/smartContainer";
import SectionHeader from "./SectionHeader";

export default function ProblemSection() {
  return (
    <section className="py-24 sm:py-32 bg-slate-50/60 border-y border-slate-200/70" data-testid="problem-section">
      <div className="dm-container">
        <SectionHeader
          eyebrow="The Problem"
          title="Buying from multiple manufacturers shouldn't feel like managing multiple companies."
          description="Traditional multi-supplier sourcing fragments every step. SmartContainer™ collapses it into a single coordinated procurement operation."
        />
        <div className="mt-16 grid lg:grid-cols-[1fr_auto_1fr] gap-6 lg:gap-4 items-stretch">
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={viewport}
            className="dm-card p-7" data-testid="problem-fragmented">
            <div className="flex items-center gap-3 mb-6">
              <span className="grid place-items-center h-9 w-9 rounded-lg bg-red-50 border border-red-100">
                <X className="h-4 w-4 text-red-500" strokeWidth={2.5} />
              </span>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-red-500">Fragmented sourcing</div>
                <div className="text-base font-bold text-slate-900">Many suppliers, many threads</div>
              </div>
            </div>
            <ul className="space-y-3">
              {FRAGMENTED.map((item) => (
                <motion.li variants={fadeUp} key={item} className="flex items-center gap-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-300 shrink-0" />
                  <span className="text-sm text-slate-500 line-through decoration-slate-300">{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <div className="hidden lg:flex items-center justify-center">
            <div className="grid place-items-center h-11 w-11 rounded-full bg-white border border-slate-200 shadow-sm">
              <ArrowRight className="h-5 w-5 text-blue-600" strokeWidth={2} />
            </div>
          </div>

          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={viewport}
            className="dm-card dm-card-hover p-7 ring-1 ring-blue-500/10" data-testid="problem-consolidated">
            <div className="flex items-center gap-3 mb-6">
              <span className="grid place-items-center h-9 w-9 rounded-lg bg-blue-50 border border-blue-100">
                <Check className="h-4 w-4 text-blue-600" strokeWidth={2.5} />
              </span>
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-blue-600">One SmartContainer™ Workspace</div>
                <div className="text-base font-bold text-slate-900">One coordinated operation</div>
              </div>
            </div>
            <ul className="space-y-3">
              {CONSOLIDATED.map((item) => (
                <motion.li variants={fadeUp} key={item} className="flex items-center gap-3">
                  <span className="grid place-items-center h-4 w-4 rounded-full bg-emerald-50 shrink-0">
                    <Check className="h-2.5 w-2.5 text-emerald-600" strokeWidth={3} />
                  </span>
                  <span className="text-sm font-medium text-slate-700">{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
